import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { CreditCard, Calendar, AlertCircle, Clock, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';

export default function SubscriptionManagement() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState(null);
  const [trialEligibility, setTrialEligibility] = useState(null);
  const [loading, setLoading] = useState(true);
  const [canceling, setCanceling] = useState(false);
  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    fetchSubscription();
  }, []);

  const fetchSubscription = async () => {
    try {
      const response = await api.get('/subscriptions/current');
      setSubscription(response.data.subscription);
      setTrialEligibility(response.data.trial_eligibility);
      
      // AUDIT LOG: Debug cancel button visibility
      if (response.data.audit) {
        console.log('🔍 SUBSCRIPTION_AUDIT (Frontend):', response.data.audit);
      }
      if (response.data.backfill_debug) {
        console.log('🔧 BACKFILL_DEBUG:', response.data.backfill_debug);
      }
      if (response.data.sync_debug) {
        console.log('🔄 SYNC_DEBUG:', response.data.sync_debug);
      }
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    setCanceling(true);
    try {
      const response = await api.post('/subscriptions/cancel', {
        reason: 'User requested cancellation'
      });
      
      if (response.data.pending) {
        toast.info('Cancellation is being processed. You can continue using all features.');
      } else if (response.data.immediate_effect) {
        toast.success('Subscription cancelled. You now have free plan access.');
      } else {
        toast.success(t('subscription.cancelSuccess'));
      }
      
      // Update local state
      setSubscription({
        ...subscription,
        cancel_at_period_end: true,
        subscription_state: response.data.subscription_state,
        stripe_sync_status: response.data.stripe_updated ? 'synced' : 'pending'
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || t('subscription.cancelFailed'));
    } finally {
      setCanceling(false);
    }
  };

  const handleRetryCancel = async () => {
    setRetrying(true);
    try {
      const response = await api.post('/subscriptions/retry-cancel');
      
      if (response.data.success) {
        toast.success('Cancellation processed successfully!');
        setSubscription({
          ...subscription,
          subscription_state: response.data.subscription_state,
          stripe_sync_status: 'synced'
        });
      } else {
        if (response.data.contact_support) {
          toast.error('Please contact support for assistance with your cancellation.');
        } else {
          toast.error(response.data.message);
        }
      }
    } catch (error) {
      toast.error('Retry failed. Please try again later.');
    } finally {
      setRetrying(false);
    }
  };

  const handleChangePlan = () => {
    navigate('/pricing');
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('subscription.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-500">{t('common.loading')}</p>
        </CardContent>
      </Card>
    );
  }

  const isFreePlan = !subscription || user?.plan === 'free';
  const planName = user?.plan?.replace('_', ' ') || 'free';
  const planTier = planName.includes('basic') ? 'basic' : planName.includes('pro') ? 'pro' : 'free';
  const billingInterval = planName.includes('monthly') ? t('pricing.monthly') : planName.includes('yearly') ? t('pricing.yearly') : null;
  
  // Enhanced state handling
  const subscriptionState = subscription?.subscription_state || 'active';
  const isInTrial = subscription?.is_trial || false;
  const trialDaysRemaining = subscription?.trial_days_remaining;
  const isPending = subscriptionState === 'pending_cancellation';
  const isScheduledCancel = subscriptionState === 'scheduled_cancel' || subscription?.cancel_at_period_end;
  
  // ENHANCED: Determine if cancel button should be shown - ensure it works for ALL paid plans
  // The button should be visible for ANY paid plan (basic_monthly, basic_yearly, pro_monthly, pro_yearly)
  // regardless of trial status, as long as it's not already cancelled or pending
  const isPaidPlan = user?.plan && user.plan !== 'free';
  const hasActiveSubscription = subscription !== null;
  const isNotCancelled = !isScheduledCancel && !isPending;
  
  const showCancelButton = isPaidPlan && hasActiveSubscription && isNotCancelled;
  
  // AUDIT LOG: Debug cancel button visibility in frontend
  console.log('🔍 CANCEL_BUTTON_DEBUG:', {
    userPlan: user?.plan,
    isPaidPlan,
    hasActiveSubscription,
    subscriptionState,
    isInTrial,
    isPending,
    isScheduledCancel,
    isNotCancelled,
    showCancelButton,
    reason: !showCancelButton ? (
      !isPaidPlan ? 'not_paid_plan' :
      !hasActiveSubscription ? 'no_subscription' :
      !isNotCancelled ? 'already_cancelled_or_pending' :
      'unknown'
    ) : 'visible'
  });
  
  // Status display logic
  const getStatusDisplay = () => {
    if (!isPaidPlan) {
      return {
        text: t('subscription.free'),
        color: 'bg-gray-600',
        icon: null
      };
    }
    
    if (isPending) {
      return {
        text: 'Processing...',
        color: 'bg-yellow-600',
        icon: <Clock className="h-3 w-3" />
      };
    }
    
    if (isInTrial && trialDaysRemaining !== null) {
      return {
        text: `Trial (${trialDaysRemaining} days left)`,
        color: 'bg-blue-600',
        icon: null
      };
    }
    
    if (isScheduledCancel) {
      return {
        text: 'Cancels soon',
        color: 'bg-amber-600',
        icon: <AlertCircle className="h-3 w-3" />
      };
    }
    
    return {
      text: t('subscription.active'),
      color: 'bg-emerald-600',
      icon: null
    };
  };

  const statusDisplay = getStatusDisplay();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CreditCard className="h-5 w-5" />
          {t('subscription.title')}
        </CardTitle>
        <CardDescription>{t('subscription.description')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Current Plan */}
        <div className="border rounded-lg p-4 bg-gradient-to-r from-emerald-50 to-blue-50">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-semibold capitalize">
              {planTier} {billingInterval && `(${billingInterval})`}
            </h3>
            <span className={`px-3 py-1 text-white text-sm font-semibold rounded-full flex items-center gap-1 ${statusDisplay.color}`}>
              {statusDisplay.icon}
              {statusDisplay.text}
            </span>
          </div>
          
          {isPaidPlan && subscription && (
            <div className="space-y-2 text-sm text-gray-600">
              {isPending && (
                <div className="flex items-center gap-2 text-yellow-600">
                  <Clock className="h-4 w-4" />
                  <span>Cancellation is being processed. You can continue using all features.</span>
                </div>
              )}
              
              {isScheduledCancel && !isPending && (
                <div className="flex items-center gap-2 text-amber-600">
                  <AlertCircle className="h-4 w-4" />
                  <span>{t('subscription.cancelsAt')}: {new Date(subscription.current_period_end).toLocaleDateString()}</span>
                </div>
              )}
              
              {!isScheduledCancel && !isPending && (
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <span>
                    {isInTrial ? 'Trial ends' : t('subscription.renewsOn')}: {new Date(subscription.current_period_end).toLocaleDateString()}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            onClick={handleChangePlan}
            variant="default"
            className="flex-1"
          >
            {!isPaidPlan ? t('subscription.upgrade') : t('subscription.changePlan')}
          </Button>

          {/* Retry button for pending cancellations */}
          {isPending && (
            <Button
              onClick={handleRetryCancel}
              disabled={retrying}
              variant="outline"
              className="flex-1 flex items-center gap-2"
            >
              <RefreshCw className={`h-4 w-4 ${retrying ? 'animate-spin' : ''}`} />
              {retrying ? 'Retrying...' : 'Retry Cancellation'}
            </Button>
          )}

          {/* Cancel button */}
          {showCancelButton && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" className="flex-1">
                  {t('subscription.cancel')}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t('subscription.confirmCancel')}</AlertDialogTitle>
                  <AlertDialogDescription>
                    {isInTrial ? (
                      <>
                        Your trial will continue until {new Date(subscription.current_period_end).toLocaleDateString()}, 
                        but you won't be charged when it ends.
                        <br /><br />
                        <strong>You'll have access until: {new Date(subscription.current_period_end).toLocaleDateString()}</strong>
                      </>
                    ) : (
                      <>
                        {t('subscription.cancelDescription')}
                        <br /><br />
                        <strong>{t('subscription.accessUntil')}: {new Date(subscription.current_period_end).toLocaleDateString()}</strong>
                      </>
                    )}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleCancelSubscription}
                    disabled={canceling}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    {canceling ? t('common.loading') : t('subscription.confirmCancelButton')}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>

        {/* Trial Eligibility Info */}
        {!isPaidPlan && trialEligibility && !trialEligibility.eligible && (
          <div className="text-sm text-amber-600 border border-amber-200 rounded-lg p-3 bg-amber-50">
            <p>
              <strong>Testphase bereits genutzt:</strong> Sie haben bereits Ihre 30-tägige kostenlose Testphase verwendet. 
              {trialEligibility.previous_trial && (
                <span> (Genutzt am {new Date(trialEligibility.previous_trial.date).toLocaleDateString('de-DE')})</span>
              )}
            </p>
            <p className="mt-1 text-xs text-amber-700">
              Für weitere Funktionen wählen Sie bitte einen kostenpflichtigen Plan.
            </p>
          </div>
        )}

        {/* Plan Features Info */}
        <div className="text-sm text-gray-500 border-t pt-4">
          <p>{t('subscription.planInfo')}</p>
        </div>
      </CardContent>
    </Card>
  );
}
